import React, { useState } from "react";
import { useRegTheme } from "../theme";
import { nodeReverseTerminalPayment } from "../data/api";
import { clearActiveOrder } from "../data/activeOrder";
import { ToastifyAlert } from "../../../CommonComponents/ToastifyAlert";
import { fmt } from "../uiUtils";

// Shown on register load when an unfinished order with a real card charge is found (mid-payment
// crash / abandoned split). The cashier RESUMES it (finish collecting + save) or DISCARDS it, in
// which case any card charge is reversed so nothing is left orphaned.
export default function RecoveryModal({ order, onResume, onDiscarded }) {
  const t = useRegTheme();
  const [busy, setBusy] = useState(false);
  const snap = (order && order.snapshot) || {};
  const lines = snap.lines || [];
  const payments = snap.payments || [];
  const itemCount = lines.reduce((n, l) => n + (Number(l.orderCount) || 0), 0);
  const grand = Number(snap.totalsGrand || 0);
  const paid = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const remaining = Math.max(Math.round((grand - paid) * 100) / 100, 0);
  const cardCharges = payments.filter((p) => String(p.payType).toLowerCase() !== "cash" && Number(p.amount) > 0);

  const discard = async () => {
    setBusy(true);
    let reversedAll = true;
    for (const c of cardCharges) {
      if (!c.paymentId) {
        reversedAll = false;
        continue;
      }
      try {
        const res = await nodeReverseTerminalPayment({ paymentId: c.paymentId, amount: c.amount });
        if (!res || !(res.status === true || res.status === "true")) reversedAll = false;
      } catch {
        reversedAll = false;
      }
    }
    await clearActiveOrder(order.orderId);
    setBusy(false);
    ToastifyAlert(
      reversedAll ? "Unfinished order discarded, card charge reversed" : "Order discarded — verify/refund the card charge manually",
      reversedAll ? "success" : "error"
    );
    onDiscarded();
  };

  const s = styles(t);
  return (
    <div className="qv-dlg-overlay" style={s.overlay}>
      <div className="qv-dlg-card" style={s.card}>
        <div style={s.head}>Unfinished order found</div>
        <div style={s.body}>
          <p style={s.msg}>A previous sale wasn't completed. A card charge was already taken — resume it to finish, or discard it (the charge will be reversed).</p>
          <div style={s.sumRow}><span style={s.muted}>Items</span><span>{itemCount}</span></div>
          <div style={s.sumRow}><span style={s.muted}>Order total</span><span>{fmt(grand)}</span></div>
          <div style={s.sumRow}><span style={s.muted}>Already paid</span><span>{fmt(paid)}</span></div>
          <div style={s.balRow}><span>Balance remaining</span><span style={s.bal}>{fmt(remaining)}</span></div>
          {cardCharges.map((c, i) => (
            <div key={i} style={s.chg}>{c.cardType || "Card"} {fmt(c.amount)}{c.paymentId ? "" : " (unverified)"}</div>
          ))}
        </div>
        <div style={s.foot}>
          <button style={s.discard} disabled={busy} onClick={discard}>{busy ? "…" : "Discard & reverse"}</button>
          <button style={s.resume} disabled={busy} onClick={() => onResume(order)}>Resume order</button>
        </div>
      </div>
    </div>
  );
}

const styles = (t) => ({
  overlay: { position: "fixed", inset: 0, background: "rgba(2,6,23,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2300 },
  card: { width: "100%", maxWidth: 440, background: t.card || "#0f1a2f", color: t.text || "#E5EDF7", borderRadius: 16, border: `1px solid ${t.cardBorder || "#1f2c47"}`, overflow: "hidden", display: "flex", flexDirection: "column" },
  head: { padding: "14px 18px", fontWeight: 800, fontSize: 16, borderBottom: `1px solid ${t.divider}`, background: "rgba(234,179,8,0.15)", color: "#EAB308" },
  body: { padding: 18, display: "flex", flexDirection: "column", gap: 8 },
  msg: { fontSize: 14, color: t.textMuted, margin: 0, marginBottom: 6 },
  sumRow: { display: "flex", justifyContent: "space-between", fontSize: 14 },
  muted: { color: t.textMuted },
  balRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4, paddingTop: 8, borderTop: `1px solid ${t.divider}`, fontWeight: 500 },
  bal: { fontSize: 20, fontWeight: 800, color: t.price || "#4C8DFF" },
  chg: { fontSize: 12, color: t.textMuted, marginTop: 2 },
  foot: { padding: 16, display: "flex", gap: 10, borderTop: `1px solid ${t.divider}` },
  discard: { flex: 1, height: 50, borderRadius: 12, border: `1px solid ${t.cardBorder}`, background: "transparent", color: "#EF4444", fontWeight: 500, cursor: "pointer" },
  resume: { flex: 1.4, height: 50, borderRadius: 12, border: "none", background: "#059669", color: "#fff", fontWeight: 800, fontSize: 16, cursor: "pointer" },
});
